import { FiLoader } from 'react-icons/fi';
import { useCurrency } from '../../context/CurrencyContext';
import { getBookImage, handleBookImageError } from '../../utils';
import StockOverlay from '../common/StockOverlay';

export const GlobalSearchResultsList = ({
  loading,
  searchError,
  results,
  query,
  activeIndex,
  onBookClick,
  onViewAll,
  showNoResults,
}) => {
  const { formatBookPrice, getBookAmount, getBookDiscount } = useCurrency();

  if (loading) {
    return (
      <p className="p-4 text-center text-sm text-black flex items-center justify-center gap-2">
        <FiLoader className="animate-spin" /> Searching...
      </p>
    );
  }

  if (searchError) {
    return <p className="p-4 text-center text-sm text-red-600">{searchError}</p>;
  }

  if (showNoResults) {
    return (
      <p className="p-6 text-center">
        <span className="text-black text-sm block">No products found for "{query}"</span>
        <span className="text-xs text-black mt-1 block">
          Try a different title, author, or category
        </span>
      </p>
    );
  }

  if (results.length === 0) return null;

  return (
    <ul className="p-2 list-none">
      <li className="text-xs text-black px-3 py-1 font-medium">
        Search Results ({results.length})
      </li>
      {results.map((book, index) => (
        <li
          key={book.id}
          role="option"
          aria-selected={activeIndex === index}
          onClick={() => onBookClick(book)}
          className={`flex items-center gap-3 p-2 rounded-md cursor-pointer transition ${
            activeIndex === index
              ? 'bg-orange-50 border border-orange-200'
              : 'hover:bg-gray-50'
          }`}
        >
          <span className="relative h-14 w-10 shrink-0 overflow-hidden rounded">
            <img
              src={getBookImage(book.image)}
              onError={handleBookImageError}
              alt={book.title || ''}
              className="h-full w-full object-cover"
            />
            <StockOverlay stock={book.stock} compact />
          </span>
          <span className="flex-1 min-w-0">
            <span className="text-sm font-medium text-black truncate block">
              {book.title}
            </span>
            <span className="text-xs text-black block">{book.author}</span>
            <span className="flex items-center gap-2 mt-0.5">
              <span className="text-xs font-semibold text-green-700">
                {formatBookPrice(book)}
              </span>
              {getBookAmount(book, 'originalPrice') > getBookAmount(book) ? (
                <span className="text-xs line-through text-black">
                  {formatBookPrice(book, 'originalPrice')}
                </span>
              ) : null}
              {getBookDiscount(book) > 0 ? (
                <span className="text-xs text-orange-600">({getBookDiscount(book)}% off)</span>
              ) : null}
            </span>
          </span>
          {book.category?.name ? (
            <span className="text-[10px] bg-gray-100 text-black px-2 py-0.5 rounded">
              {book.category.name}
            </span>
          ) : null}
        </li>
      ))}
      <li>
        <button
          type="button"
          onClick={onViewAll}
          className="w-full mt-2 text-center text-sm text-orange-600 font-medium py-2 hover:bg-orange-50 rounded-md transition"
        >
          View all results for "{query}"
        </button>
      </li>
    </ul>
  );
};
